import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";
import {
    AgentSkillsView,
    AgentSkillsViewColumn,
    AgentSkillsViewSchema,
} from "@common/types/views/agentSkillsView";
import { View } from "@common/types/views/views";
import {
    WORKSPACE_USER_CACHE_KEYS,
    useWorkspaceUserCache,
} from "@hooks/useWorkspaceUserCache";
import { ensureViewColumn, mergeCachedView } from "@lib/mergeCachedView";
import { defaultAgentSkillsView } from "./agentSkillsView";
import { agentSkillsViewConfig } from "./agentSkillsViewConfig";

const OFFERED_COLUMNS = [
    AgentSkillsViewColumn.AVAILABLE_IN,
    AgentSkillsViewColumn.UPDATED_BY,
];

/**
 * Saved layouts that predate the Available in and Updated by columns get
 * them added once, so existing users see them without resetting their
 * settings. Hiding them afterwards sticks.
 */
export function useAgentSkillsView() {
    const [cachedView, setCachedView] = useWorkspaceUserCache<AgentSkillsView>(
        WORKSPACE_USER_CACHE_KEYS.AGENT_SKILLS_PAGE_VIEW_CACHE_KEY,
        AgentSkillsViewSchema,
    );
    const [wereColumnsOffered, setColumnsOffered] =
        useWorkspaceUserCache<boolean>(
            WORKSPACE_USER_CACHE_KEYS.AGENT_SKILLS_LIST_COLUMNS_OFFERED_CACHE_KEY,
            z.boolean(),
        );
    const isColumnOfferPending =
        cachedView !== undefined && wereColumnsOffered !== true;
    const isColumnOfferPendingRef = useRef(isColumnOfferPending);
    const [view, setView] = useState<AgentSkillsView>(() => {
        const mergedView = mergeCachedView(
            cachedView,
            defaultAgentSkillsView,
            agentSkillsViewConfig,
        );
        return isColumnOfferPending
            ? OFFERED_COLUMNS.reduce(ensureViewColumn, mergedView)
            : mergedView;
    });

    useEffect(() => {
        if (!isColumnOfferPendingRef.current) {
            return;
        }
        isColumnOfferPendingRef.current = false;
        setCachedView(view);
        setColumnsOffered(true);
    }, [view, setCachedView, setColumnsOffered]);

    const handleViewChange = useCallback(
        (newView: View) => {
            const parsed = AgentSkillsViewSchema.safeParse(newView);
            if (!parsed.success) {
                return;
            }
            setView(parsed.data);
            setCachedView(parsed.data);
            setColumnsOffered(true);
        },
        [setCachedView, setColumnsOffered],
    );

    return { view, handleViewChange };
}
